import { computed, type ComputedRef } from "vue";
import { getCountryCodeForRegionCode, getSupportedRegionCodes } from "awesome-phonenumber";
import { useLocale } from "./useLocale";
import type { SupportedLocale } from "@/i18n";

export interface Country {
  iso2: string;
  dialCode: string;
  name: string;
}

/**
 * Derive a default region code from the UI locale. Used for the initial
 * country selection when the app first mounts.
 */
export function defaultCountryForLocale(locale: SupportedLocale): string {
  switch (locale) {
    case "pt-BR":
      return "BR";
    case "es-ES":
      return "ES";
    case "en-US":
    default:
      return "US";
  }
}

/**
 * Localized country list. Names come from Intl.DisplayNames in the current
 * UI locale; dial codes come from awesome-phonenumber's metadata so the two
 * sources stay in sync with the validation path.
 */
export function useCountries(): { countries: ComputedRef<Country[]> } {
  const { locale } = useLocale();

  const countries = computed<Country[]>(() => {
    const displayNames = new Intl.DisplayNames([locale.value], { type: "region" });
    const collator = new Intl.Collator(locale.value, { sensitivity: "base" });
    const list: Country[] = [];
    for (const iso2 of getSupportedRegionCodes()) {
      const callingCode = getCountryCodeForRegionCode(iso2);
      if (!callingCode) continue;
      const name = displayNames.of(iso2) ?? iso2;
      list.push({ iso2, dialCode: String(callingCode), name });
    }
    list.sort((a, b) => collator.compare(a.name, b.name));
    return list;
  });

  return { countries };
}
